import { Link } from 'react-router'
import { ImageFallback } from '#app/components/image-fallback.tsx'
import { Card } from '#app/components/ui/card.tsx'
import { glassControlClassName } from '#app/components/ui/glass.ts'
import { Icon } from '#app/components/ui/icon.tsx'
import { getLocalizedField } from '#app/utils/blog-i18n.ts'
import { cn, getShopImgSrc } from '#app/utils/misc.tsx'
import { type getAvailableProducts } from '#app/utils/shop.server.ts'

export type ProductWithRelations = Awaited<
	ReturnType<typeof getAvailableProducts>
>[number]

export function ProductCard({
	product,
	headingLevel: Heading = 'h2',
}: {
	product: ProductWithRelations
	/** `h3` where the cards sit under a section heading */
	headingLevel?: 'h2' | 'h3'
}) {
	const name = getLocalizedField(product, 'name', 'de')
	const heroImage = product.images[0]
	const lowestPrice =
		product.variants.length > 0
			? Math.min(...product.variants.map((v) => v.finalPrice))
			: null

	return (
		<Card asChild interactive className="group h-full gap-0 overflow-hidden p-0">
			<Link to={`/de/shop/${product.category.slug}/${product.slug}`}>
				{/* Image */}
				<div className="relative block aspect-video overflow-hidden">
					{heroImage ? (
						<img
							src={getShopImgSrc(heroImage.objectKey)}
							alt={getLocalizedField(heroImage, 'altText', 'de') || name}
							className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.06]"
							loading="lazy"
						/>
					) : (
						<ImageFallback label={name} />
					)}
					{/* Subtle bottom fade for depth */}
					<div
						className="from-card/60 pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t to-transparent opacity-60 transition-opacity duration-300 group-hover:opacity-30"
						aria-hidden="true"
					/>
					{/* Category badge — floats over image */}
					<span
						className={cn(
							glassControlClassName,
							'text-body-2xs text-primary absolute top-3 left-3 inline-block rounded-full border px-2.5 py-0.5 font-medium shadow-sm',
						)}
					>
						{getLocalizedField(product.category, 'name', 'de')}
					</span>
				</div>

				{/* Content */}
				<div className="flex flex-1 flex-col p-5">
					{/* Title */}
					<Heading className="text-body-md group-hover:text-primary mb-3 leading-snug font-semibold transition-colors">
						{name}
					</Heading>

					{/* Price + CTA affordance */}
					<div className="border-border/30 mt-auto flex items-end justify-between gap-2 border-t pt-3">
						{lowestPrice !== null && lowestPrice > 0 ? (
							<p className="text-body-sm text-foreground font-semibold">
								<span className="text-body-2xs text-muted-foreground font-normal">
									ab{' '}
								</span>
								CHF {lowestPrice.toFixed(2)}
							</p>
						) : product.purchaseType === 'INQUIRY' ? (
							<p className="text-body-xs text-muted-foreground">
								Preis auf Anfrage
							</p>
						) : (
							<span />
						)}
						<Icon
							name="arrow-right"
							className="text-muted-foreground group-hover:text-primary size-4 transition-all duration-300 group-hover:translate-x-0.5"
							aria-hidden="true"
						/>
					</div>
				</div>
			</Link>
		</Card>
	)
}
